import { usePaymentLoadingStore } from "@/paymentLoadingStore";

/**
 * How long we give the redirect to take effect before revealing the manual
 * "return to the app" button.
 *
 * The page surviving *is* the signal: a handoff that works either unloads the
 * document or hides it within a few frames, so anything still visible after a
 * beat was blocked. Be generous rather than clever — a button shown late costs
 * nothing, never showing it strands a payer who has already been charged.
 */
const FALLBACK_DELAY_MS = 1000;

/**
 * The MATCHi app's own URL schemes: `matchi://` in production builds and
 * `matchi.<variant>://` in the preview and dev builds (see `scheme` in the
 * mobileapp's app.config.js, where APP_VARIANT is production | preview | dev).
 *
 * Spelled out rather than pattern-matched: `returnToAppUrl` ends up as a
 * user-facing `href`, so only schemes an actual MATCHi build registers belong
 * here. A new variant has to be added here too. The list is also half of the
 * `onSuccessUrl` allow-list in `successTarget.ts`, so adding a scheme here also
 * widens where checkout may redirect after payment.
 */
export const APP_SCHEMES = ["matchi:", "matchi.preview:", "matchi.dev:"];

/**
 * The fallback is for app handoffs only — nothing else can silently fail to
 * navigate. Chrome refuses to hand a custom-scheme intent to an app without a
 * user gesture and parks it behind an easy-to-miss interstitial instead.
 *
 * Everything web stays out of it:
 * - the widget passes its own page as `onSuccessUrl` (https),
 * - the iOS app passes an https sentinel URL it intercepts in its WebView,
 * - and http(s) navigations always either happen or fail visibly, so a slow
 *   server would otherwise trip the timer on a perfectly healthy redirect.
 *
 * If Android returns ever move to https (verified App Links), this check has to
 * be revisited.
 */
const isAppReturnUrl = (url: string) => hasScheme(url, APP_SCHEMES);

/**
 * Whether `url` uses one of `schemes`, parsed the way the browser will parse it
 * when navigating — so leading whitespace, tabs or mixed case can't disguise
 * the real scheme. `URL.protocol` is always lowercase.
 */
export const hasScheme = (url: string, schemes: string[]) => {
  try {
    return schemes.includes(new URL(url, window.location.href).protocol);
  } catch {
    return false;
  }
};

/**
 * One attempt per target for the lifetime of the document, so re-renders can't
 * re-fire the navigation or stack up timers. This guards the *navigation* only —
 * the fallback below re-arms itself from within a single attempt.
 */
let attemptedUrl: string | null = null;

/**
 * Navigate to a post-payment redirect target and, for app handoffs, arm a short
 * fallback that reveals a manual return button if the navigation never happens.
 *
 * CALL THIS ONLY ON A TERMINAL, BACKEND-CONFIRMED PAYMENT STATE. The app treats
 * arrival of `onSuccessUrl` as proof of payment and does not re-check, so the
 * button must never become reachable before the backend has confirmed the
 * order. Arming the timer here — in the same code path as the redirect — is
 * what guarantees that: no mount effect, no visibility listener, no generic
 * "waiting" spinner can put the button on screen.
 */
export const redirectWithReturnFallback = (url: string): void => {
  if (attemptedUrl === url) return;
  attemptedUrl = url;

  if (!isAppReturnUrl(url)) {
    window.location.assign(url);
    return;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  const cancel = () => {
    clearTimeout(timer);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", cancel);
  };

  const arm = () => {
    timer = setTimeout(() => {
      // Hidden: either the handoff took, or something unrelated pulled the
      // document into the background — Chrome's own blocked-intent
      // interstitial, the notification shade, an incoming call, a screen lock,
      // an app switch. We can't tell those apart, so concede nothing: stay
      // armed and let handleVisibilityChange try again if the payer comes back.
      // A button revealed late in an abandoned tab is inert; never revealing it
      // strands a payer who has already been charged.
      if (document.visibilityState === "hidden") return;

      cancel();

      // Still here, still visible: the redirect was blocked. Drop the spinner
      // it left behind and offer the tap that Chrome wants.
      usePaymentLoadingStore.setState({
        returnToAppUrl: url,
        isLoading: false,
        paymentFormSubmitting: false,
        verifyingRedirectResult: false,
      });
    }, FALLBACK_DELAY_MS);
  };

  const handleVisibilityChange = () => {
    // Never fire while hidden, but always give the payer a fresh beat once they
    // are looking at the page again. Safe under the contract above: the order is
    // confirmed before we are ever called, so a later visible event cannot
    // reveal the button prematurely.
    clearTimeout(timer);
    if (document.visibilityState === "visible") arm();
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  // A document that is actually going away is the one signal we can trust — the
  // handoff took, so this is a hard cancel with no re-arming.
  window.addEventListener("pagehide", cancel);

  arm();

  window.location.assign(url);
};
